import {
  AnimatedNavigationSwitch,
  FullPageCard,
  LazyPage,
  NavigationPage,
} from '@/app/components'
import SurveyObserver from '@/app/components/SurveyObserver'
import { useNavigation } from '@/app/hooks'
import {
  pageWithErrorBoundary,
  LocationTag,
} from '@/authenticated/components/ErrorBoundary'
import { LoadingAnimation } from '@/components'
import { useAppSelector } from '@/hooks'
import { selectUnenrollmentProgress } from '@/reducers/inProgressUtilityProgramUnenrollments'
import { programsNewCollection } from '@/reducers/programsNew'
import { searchableUtilityProgramsCollection } from '@/reducers/utilityPrograms'
import { UtilityProgram } from '@/types/utilityProgram'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { Route, useLocation, useParams } from 'react-router-dom'
import { match, P } from 'ts-pattern'

const UnenrollDeviceSelect = React.lazy(
  () => import('./unenroll-device-select/UnenrollDeviceSelect'),
)
const UnenrollDeviceSelectPage = pageWithErrorBoundary(
  LazyPage(UnenrollDeviceSelect),
  {
    location: LocationTag.UnenrollDeviceSelect,
    functionalityDescription: 'Unenroll Device Selection Page',
    severity: 'critical',
  },
)
const UnenrollReason = React.lazy(
  () => import('./unenroll-reason/UnenrollReason'),
)
const UnenrollReasonPage = pageWithErrorBoundary(LazyPage(UnenrollReason), {
  location: LocationTag.UnenrollReason,
  functionalityDescription: 'Unenrollment Reason Page',
  severity: 'critical',
})
const UnenrollSuggestions = React.lazy(
  () => import('./suggestions/UnenrollSuggestions'),
)
const UnenrollSuggestionsPage = pageWithErrorBoundary(
  LazyPage(UnenrollSuggestions),
  {
    location: LocationTag.UnenrollSuggestions,
    functionalityDescription: 'Unenrollment Suggestions Page',
    severity: 'critical',
  },
)
const UnenrollFeedback = React.lazy(
  () => import('./unenroll-feedback/unenroll-feedback'),
)
const UnenrollFeedbackPage = pageWithErrorBoundary(LazyPage(UnenrollFeedback), {
  location: LocationTag.UnenrollFeedback,
  functionalityDescription: 'Unenrollment Feedback Page',
  severity: 'critical',
})
const UnenrollReview = React.lazy(
  () => import('./unenroll-review/UnenrollReview'),
)
const UnenrollReviewPage = pageWithErrorBoundary(LazyPage(UnenrollReview), {
  location: LocationTag.UnenrollReview,
  functionalityDescription: 'Review Unenrollment Page',
  severity: 'critical',
})
const UnenrollState = React.lazy(
  () => import('./unenroll-state/unenroll-state'),
)
const UnenrollStatePage = pageWithErrorBoundary(LazyPage(UnenrollState), {
  location: LocationTag.UnenrollState,
  functionalityDescription: 'Unenrollment State Page',
  severity: 'critical',
})

type UtilityProgramContextType = {
  utilityProgram: UtilityProgram
}

const UtilityProgramContext = createContext<UtilityProgramContextType>({
  utilityProgram: {} as UtilityProgram,
})

export const useUnenrollmentRouteContext = () =>
  useContext(UtilityProgramContext)

export default function UtilityProgramUnenrollRoot() {
  const { id: utilityProgramId } = useParams<{ id: string }>()
  const { data: utilityPrograms, isLoading } =
    searchableUtilityProgramsCollection.useFetch({
      path: `utility_programs/${utilityProgramId}`,
    })
  const utilityProgram = utilityPrograms.length ? utilityPrograms[0] : undefined

  if (!utilityProgram || isLoading) {
    return (
      <FullPageCard>
        <NavigationPage
          id="multi-program-unenroll-page-loading"
          className="!p-0"
        >
          <div className="flex flex-col gap-4 h-full">
            <section className="h-full">
              <div className={'flex justify-center items-center h-full'}>
                <LoadingAnimation type="falling" />
              </div>
            </section>
          </div>
        </NavigationPage>
      </FullPageCard>
    )
  }

  return (
    <UtilityProgramContext.Provider value={{ utilityProgram }}>
      <SurveyObserver observedSurveys={['ProgramUnenrollment']}>
        <UnenrollmentRoot />
      </SurveyObserver>
    </UtilityProgramContext.Provider>
  )
}

function UnenrollmentRoot() {
  const { isLoading } = programsNewCollection.useFetch()
  const { utilityProgram } = useUnenrollmentRouteContext()
  const location = useLocation()
  const navigate = useNavigation()
  const { totalSteps, completedSteps } = useAppSelector(
    selectUnenrollmentProgress(utilityProgram.id),
  )

  const currentStep = useMemo(
    () =>
      match(location.pathname)
        .with(P.string.endsWith('/device-select'), () => 1)
        .with(P.string.endsWith('/reason'), () => 2)
        .with(P.string.endsWith('/suggestions'), () => undefined)
        .with(P.string.endsWith('/feedback'), () => 3)
        .with(P.string.endsWith('/review'), () => 4)
        .with(P.string.includes('/state'), () => undefined)
        .otherwise(() => 1),
    [location.pathname, completedSteps],
  )

  // reset user to first step if they get to a step out of order
  useEffect(() => {
    if (
      completedSteps === 0 &&
      !location.pathname.includes('/device-select') &&
      !location.pathname.includes('/state')
    ) {
      navigate.push(
        `/utility-program/${utilityProgram.id}/unenroll/device-select`,
      )
    }
  }, [completedSteps, location.pathname])

  const onCloseClick = () => {
    navigate.push(`/utility-program/${utilityProgram.id}/program-details`)
  }

  return (
    <FullPageCard>
      <NavigationPage
        id="utility-program-unenroll-root"
        navigationOptions={
          !location.pathname.includes('/state')
            ? ['back', 'close']
            : ['nothing']
        }
        onCloseClick={onCloseClick}
        className="!p-0"
        progressBar={
          currentStep !== undefined
            ? {
                completedSteps: currentStep,
                totalSteps: totalSteps + 1,
              }
            : undefined
        }
      >
        <div className="flex flex-col gap-4 h-full">
          <section className="h-full">
            {isLoading ? (
              <div className={'flex justify-center items-center h-full'}>
                <LoadingAnimation type={'falling'} />
              </div>
            ) : (
              <AnimatedNavigationSwitch>
                <Route path="/utility-program/:id/unenroll/device-select">
                  <UnenrollDeviceSelectPage />
                </Route>
                <Route path="/utility-program/:id/unenroll/reason">
                  <UnenrollReasonPage />
                </Route>
                <Route path="/utility-program/:id/unenroll/suggestions">
                  <UnenrollSuggestionsPage />
                </Route>
                <Route path="/utility-program/:id/unenroll/feedback">
                  <UnenrollFeedbackPage />
                </Route>
                <Route path="/utility-program/:id/unenroll/review">
                  <UnenrollReviewPage />
                </Route>
                <Route path="/utility-program/:id/unenroll/state/:state">
                  <UnenrollStatePage />
                </Route>
              </AnimatedNavigationSwitch>
            )}
          </section>
        </div>
      </NavigationPage>
    </FullPageCard>
  )
}
